import {Fragment, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteIcon from '@mui/icons-material/Delete'
import Divider from '@mui/material/Divider'
import GroupIcon from '@mui/icons-material/Group'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import PersonIcon from '@mui/icons-material/Person'
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import DeleteSetting from './DeleteSetting'
import DisableBypass from './DisableBypass'
import FlagCard from './FlagCard'
import NewFlag from './NewFlag'

import {connect} from '../../../state/hooks/tools'
import {updateResource} from '../../../lib/asyncMethods'
import {findRecordByAttribute, formatDate} from '../../../lib/data'
import {firstLetterUpperCased, fullName, isEmpty} from '../../../lib/tools'
import {DISABLED, ENABLED, TFA_BYPASS, isFlagEnabled, managedFlags, tfaBypassFlag} from '../../../lib/featureFlags'
import {SETTINGS} from '../../../state/actionTypes'

/**
 * The flags section of the admin settings page.
 *
 * Every flag is one card. A flag an admin created is named and scoped by them — left open to
 * everyone, or pointed at a single account — so the rows in the settings table are the whole list.
 * TFA_BYPASS is the exception the sign-in recognises by name.
 *
 * Granting it belongs to the account it is granted to, so that stays on the user's own page and this
 * page sends an ungranted flag there rather than offering a second way in. What is left here is the
 * switch — and switching TFA_BYPASS off locks the account out rather than asking it for a second
 * factor, so that half goes through a dialog that says as much. Any other flag can be deleted, behind
 * a dialog of its own; TFA_BYPASS never offers it, since a grant that could be removed could be moved.
 */
const TFA_BYPASS_DESCRIPTION =
  'Signs in on the password alone, and is never asked to enrol a second factor. Switched off, this account cannot sign in at all.'

const bypassStatus = flag => {
  if (isEmpty(flag))
    return {label: 'Not granted', variant: 'outlined'}

  return isFlagEnabled(flag) ? {label: 'Enabled', color: 'primary'} : {label: 'Locked out', color: 'error'}
}

const flagStatus = flag => (
  isFlagEnabled(flag) ? {label: 'Enabled', color: 'primary'} : {label: 'Disabled', variant: 'outlined'}
)

const FlagOwner = props => (
  <Stack className='owner' direction='row'>
    {props.avatar}
    <Stack>
      <Typography className='caption' variant='caption'>{props.label}</Typography>
      {props.children}
    </Stack>
  </Stack>
)

const FeatureFlags = props => {
  const [showNewFlag, setShowNewFlag] = useState(false)
  const [showDisable, setShowDisable] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [flagToDelete, setFlagToDelete] = useState({})

  const bypass = tfaBypassFlag(props.settings)
  const holder = findRecordByAttribute({records: props.users, attribute: 'id', id: bypass.ownableId})
  // the users listing is paginated, so the account holding the flag is often not among the records
  // this page was handed — naming it is a courtesy, and its absence must not read as nobody
  const holderName = isEmpty(holder) ? 'another account' : fullName(holder)
  const flags = managedFlags(props.settings)

  const toggleNewFlag = () => {
    setShowNewFlag(!showNewFlag)
  }

  const toggleDisable = () => {
    setShowDisable(!showDisable)
  }

  const toggleDelete = () => {
    setShowDelete(!showDelete)
  }

  const confirmDelete = flag => () => {
    setFlagToDelete(flag)
    toggleDelete()
  }

  const toggleFlag = flag => () => {
    props.updateResource({
      endpoint: `settings/${flag.id}`,
      attributes: {value: isFlagEnabled(flag) ? DISABLED : ENABLED}
    })
  }

  const accountOwner = ownableId => {
    const owner = findRecordByAttribute({records: props.users, attribute: 'id', id: ownableId})

    return (
      <FlagOwner
        label='Granted to'
        avatar={
          <Avatar className='holder'>
            {isEmpty(owner) ? <PersonIcon /> : firstLetterUpperCased(owner.firstName)}
          </Avatar>
        }>
          {
            isEmpty(owner) ?
              <Typography variant='body2'>another account</Typography> :
              <Link component={RouterLink} variant='body2' to={`/admin/users/${ownableId}`}>
                {fullName(owner)}
              </Link>
          }
      </FlagOwner>
    )
  }

  const everyoneOwner = flag => (
    <FlagOwner label='Applies to' avatar={<Avatar className='everyone'><GroupIcon /></Avatar>}>
      <Typography variant='body2'>All users · created {formatDate(flag.createdAt)}</Typography>
    </FlagOwner>
  )

  const bypassOwner = () => {
    if (isEmpty(bypass)) {
      return (
        <FlagOwner label='Granted to' avatar={<Avatar className='nobody'><PersonOutlineIcon /></Avatar>}>
          <Typography variant='body2'>Nobody yet</Typography>
        </FlagOwner>
      )
    }

    return accountOwner(bypass.ownableId)
  }

  const bypassAction = () => {
    if (isEmpty(bypass)) {
      return (
        <Link component={RouterLink} to='/admin/users'>
          <Button endIcon={<ChevronRightIcon />}>Grant in Users</Button>
        </Link>
      )
    }

    if (isFlagEnabled(bypass)) {
      return (
        <Button disabled={props.loading} onClick={toggleDisable} color='error' sx={{bgcolor: 'Background'}}>
          Disable
        </Button>
      )
    }

    return (
      <Button disabled={props.loading} onClick={toggleFlag(bypass)} variant='contained' sx={{color: '#F7F7F7'}}>
        Enable
      </Button>
    )
  }

  return (
    <Fragment>
      <Stack className='section'>
        <Stack direction='row' sx={{alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant='h6'>Feature Flags</Typography>
          <Button onClick={toggleNewFlag} startIcon={<AddIcon />}>New Flag</Button>
        </Stack>
        <Divider />
        {
          flags.map(flag => (
            <FlagCard
              key={flag.id}
              name={flag.name}
              enabled={isFlagEnabled(flag)}
              status={flagStatus(flag)}
              owner={flag.ownableId ? accountOwner(flag.ownableId) : everyoneOwner(flag)}
              action={
                <Stack direction='row'>
                  <IconButton disabled={props.loading} onClick={confirmDelete(flag)} color='error' aria-label='destroy'>
                    <DeleteIcon />
                  </IconButton>
                  <Button disabled={props.loading} onClick={toggleFlag(flag)} sx={{bgcolor: 'Background'}}>
                    {isFlagEnabled(flag) ? 'Disable' : 'Enable'}
                  </Button>
                </Stack>
              }
            />
          ))
        }
        {
          isEmpty(flags) && !props.loading && (
            <Stack className='empty'>
              <AddIcon color='disabled' />
              <Typography variant='body2'>
                No flags yet. <strong>New Flag</strong> names one, scopes it, and creates the row.
              </Typography>
            </Stack>
          )
        }
        <FlagCard
          name={TFA_BYPASS}
          description={TFA_BYPASS_DESCRIPTION}
          enabled={isFlagEnabled(bypass)}
          status={bypassStatus(bypass)}
          owner={bypassOwner()}
          action={bypassAction()}
        />
        {
          !isEmpty(bypass) && (
            <Alert className='notice' severity='error'>
              {
                isFlagEnabled(bypass) ?
                  <Fragment>
                    <strong>Disable</strong> locks {holderName} out of sign-in — there is no second factor
                    to fall back on, and only an admin turning this back on lets them in again.
                  </Fragment> :
                  <Fragment>
                    Sign-in is blocked for {holderName} until this is switched back on.
                  </Fragment>
              } The grant is never removed and cannot be moved to another account.
            </Alert>
          )
        }
      </Stack>
      <NewFlag showModal={showNewFlag} toggleModal={toggleNewFlag} />
      <DisableBypass
        showModal={showDisable}
        toggleModal={toggleDisable}
        flag={bypass}
        holderName={holderName}
      />
      <DeleteSetting showModal={showDelete} toggleModal={toggleDelete} setting={flagToDelete} />
    </Fragment>
  )
}

const mapStateToProps = ({metadata, users}) => ({
  users: users.users || [],
  settings: metadata.settings,
  loading: metadata.loading
})

const actionCreators = ({metadata}) => ({updateResource: updateResource(metadata, SETTINGS)})

export default connect(mapStateToProps, actionCreators)(FeatureFlags)
